import { resources } from "@constants/hardcoded-strings";
import { SERVICE_TEMPLATE_DETAILS_REFERENCE_DATA_TYPES, ServiceTemplateDetailsReferenceData } from "@constants/reference-data";
import { MANAGEMENT_CONSOLE_MASTER_DATA_SERVICE_TEMPLATES_URL } from "@constants/routes";
import { SERVICE_TEMPLATE_ID_PATTERN } from "@constants/url-parameter-patterns";
import { CardContainer } from "@containers/common/card-container/card-container";
import { ContentPageBody } from "@containers/common/content-page-body/content-page-body";
import { ContentPageHeader } from "@containers/common/content-page-header/content-page-header";
import { LoadingIndicator } from "@containers/common/loading-indicator/loading-indicator";
import { ServiceTemplateDetailsComponent } from "@containers/management-console/service-template-details/service-template-details-component/service-template-details-component";
import { CommonReferenceDataPageTypes } from "@enums/common-reference-data-page-types";
import { ReferenceDataSourceTypes } from "@enums/reference-data-source-types";
import { formatServiceTemplateDetails } from "@formatters/service-template-details/service-template-details-formatter";
import { useReferenceDataSelector } from "@hooks/use-reference-data-selector";
import { RpServiceTemplate } from "@idw-types/data/reference-data/generic";
import { ServiceTemplateDetails } from "@idw-types/data/service-template-details/service-template-details";
import { ApplicationState } from "@idw-types/state/application-state";
import { getCommonReferenceDataReduxRequest } from "@redux-requests/reference-data/get-common-reference-data-redux-request";
import { getServiceTemplateDetailsReduxRequest } from "@redux-requests/service-template-details/get-service-template-details";
import { setServiceTemplateDetails } from "@redux/actions/service-template-details-actions";
import { navigateToUtil } from "@utils/navigation-util";
import { toObject } from "@utils/object-util";
import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";

export const ServiceTemplateDetailsContainer = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const routerLocation = useLocation();
    const { serviceTemplateId } = useParams<{ [SERVICE_TEMPLATE_ID_PATTERN]: string }>();

    const {
        isLoadingServiceTemplateDetails,
        serviceTemplateDetails,
    } = useSelector((state: ApplicationState) => ({
        isLoadingServiceTemplateDetails: state.serviceTemplateDetails.data.isLoading,
        serviceTemplateDetails: state.serviceTemplateDetails.data.serviceTemplate
    }));

    const [shouldLoadServiceTemplateDetails, setShouldLoadServiceTemplateDetails] = useState(true);
    const referenceData = useReferenceDataSelector<ServiceTemplateDetailsReferenceData>(SERVICE_TEMPLATE_DETAILS_REFERENCE_DATA_TYPES);
    const [shouldLoadReferenceData, setShouldLoadReferenceData] = useState(true);
    const [unformattedServiceTemplateDetails, setUnformattedServiceTemplateDetails] = useState<ServiceTemplateDetails>(null);

    const redirectToServiceTemplatesPage = () => {
        navigateToUtil(MANAGEMENT_CONSOLE_MASTER_DATA_SERVICE_TEMPLATES_URL, navigate, routerLocation.pathname);
    };

    const getUnformattedServiceTemplateDetails = async () => {
        const response = await getServiceTemplateDetailsReduxRequest(dispatch)(serviceTemplateId, redirectToServiceTemplatesPage);
        if(response){
            setUnformattedServiceTemplateDetails(response);

            if (!referenceData.isLoading.serviceTemplates && shouldLoadReferenceData) {
                setShouldLoadReferenceData(false);
                getCommonReferenceDataReduxRequest(dispatch)(
                    SERVICE_TEMPLATE_DETAILS_REFERENCE_DATA_TYPES,
                    ReferenceDataSourceTypes.AXNADMIN_CORE,
                    CommonReferenceDataPageTypes.SERVICE_TEMPLATE_DETAILS
                );
            }
        }
    };

    useEffect(() => {
        if (!isLoadingServiceTemplateDetails && shouldLoadServiceTemplateDetails) {
            setShouldLoadServiceTemplateDetails(false);
            getUnformattedServiceTemplateDetails();
        }
    }, []);

    const isReferenceDataPresent = useMemo(() => {
        return !!(
            referenceData.axnCompanies &&
            referenceData.rpServiceTemplates
        );
    }, [
        referenceData.axnCompanies,
        referenceData.rpServiceTemplates
    ]);

    useEffect(() => {
        if (unformattedServiceTemplateDetails && isReferenceDataPresent) {
            const formattedServiceTemplateDetails = formatServiceTemplateDetails(unformattedServiceTemplateDetails, referenceData.axnCompanies);
            dispatch(setServiceTemplateDetails(formattedServiceTemplateDetails));
        }
    }, [unformattedServiceTemplateDetails, isReferenceDataPresent, referenceData.axnCompanies]);


    const isLoading = useMemo(() => {
        return (
            isLoadingServiceTemplateDetails ||
            shouldLoadServiceTemplateDetails ||
            referenceData.isLoading.serviceTemplateDetails
        );
    }, [
        isLoadingServiceTemplateDetails,
        shouldLoadServiceTemplateDetails,
        referenceData.isLoading.serviceTemplateDetails
    ]);

    const isLoaded = useMemo(() => {
        return !!(
            !isLoading &&
            serviceTemplateDetails &&
            isReferenceDataPresent
        );
    }, [
        isLoading,
        serviceTemplateDetails,
        isReferenceDataPresent
    ]);

    const serviceTemplatesMap = useMemo(() => {
        if (isReferenceDataPresent && referenceData.rpServiceTemplates.length > 0) {
            return toObject<RpServiceTemplate>(referenceData.rpServiceTemplates, 'name');
        }

        return {};
    }, [isReferenceDataPresent, referenceData.rpServiceTemplates]);

    return (

        <CardContainer>
            <ContentPageHeader title={resources.pages.serviceTemplateDetails.title} />
            <ContentPageBody isMaxWidth={true}>
                {isLoaded && (
                    <ServiceTemplateDetailsComponent
                        serviceTemplateDetails={serviceTemplateDetails}
                        serviceTemplatesMap={serviceTemplatesMap}
                        axnCompanies={referenceData.axnCompanies}
                    />
                )}
                {(isLoading) && (
                    <LoadingIndicator title={resources.common.loadingIndicatorText} />
                )}
            </ContentPageBody>
        </CardContainer>
    );
}